import { useState, useEffect, useCallback } from 'react';
import { GetSingleShipment } from '../apis/shipment';
import type { SingleShipmentDataExtended } from '../apis/types';
import useUnauthorize from './useUnauthorize';
import { useToken } from '../common/helper';
import { useSpin } from '../components/SpinContext';

interface UseFetchSingleShipmentReturn {
  shipmentData: SingleShipmentDataExtended | undefined;
  // Tracks this fetch's own in-flight state, independent of isSpin/showSpin
  // -- callers that don't want the app-global spinner can still show a
  // scoped loading state of their own from this.
  loading: boolean;
  refetchShipment: (runSpin?: boolean, includeMonitor?: boolean) => void;
}

/**
 * Hook to fetch and manage single shipment data
 *
 * @param shipmentId - The ID of the shipment to fetch.
 * @param isSpin - Whether to show loading spinner during fetch (default: false)
 * @returns An object containing:
 *   - shipmentData: The fetched shipment data or null
 *   - refetchShipment: Function to manually refetch shipment
 */
const useFetchSingleShipment = (
  shipmentId?: string,
  isSpin = false,
  sensorPayloadMode: 'full' | 'full_vitals' | 'minimal' | 'none' = 'full',
  legType?: string
): UseFetchSingleShipmentReturn => {
  const { showSpin, hideSpin } = useSpin();
  const { checkTokenExpiry } = useToken();
  const { unauthorize } = useUnauthorize();
  const [shipmentData, setShipmentData] =
    useState<SingleShipmentDataExtended>();
  const [loading, setLoading] = useState(false);

  const fetchShipment = useCallback(
    (runSpin = isSpin, includeMonitor = true) => {
      if (!shipmentId) {
        setShipmentData(undefined);
        setLoading(false);
        return;
      }
      if (runSpin) {
        showSpin();
      }
      setLoading(true);
      const token = checkTokenExpiry();
      if (!token) {
        setLoading(false);
        return;
      }
      const _t0 = performance.now();
      GetSingleShipment(
        token,
        shipmentId,
        false,
        includeMonitor,
        sensorPayloadMode,
        legType
      )
        .then((res) => {
          const data = res?.data?.data ? res.data?.data : null;
          if (data?.is_success) {
            // We always get the extended ShipmentData, because we don't
            // call GetSingleShipment with "isHome: true"
            const resp = data?.data
              ? (data?.data as SingleShipmentDataExtended)
              : undefined;
            const monitorCount = resp?.shipment_monitor?.length ?? 0;
            console.info('[shipment_detail_fetch]', {
              shipment_id: shipmentId,
              duration_ms: Math.round(performance.now() - _t0),
              monitor_point_count: monitorCount,
              include_monitor: includeMonitor,
              sensor_payload_mode: sensorPayloadMode,
            });
            if (!includeMonitor) {
              // Defensive merge for callers that omit monitor history. The
              // drawer still needs minimal sensor metadata to remain stable.
              setShipmentData((prev) =>
                resp
                  ? {
                      ...resp,
                      shipment_monitor: prev?.shipment_monitor ?? [],
                      monitor_info: resp.monitor_info?.length
                        ? resp.monitor_info
                        : prev?.monitor_info,
                      sensor_summary: resp.sensor_summary?.length
                        ? resp.sensor_summary
                        : prev?.sensor_summary,
                    }
                  : undefined
              );
            } else {
              setShipmentData(resp);
            }
          } else {
            setShipmentData(undefined);
          }
        })
        .catch((error) => {
          unauthorize(error);
          setShipmentData(undefined);
        })
        .finally(() => {
          hideSpin();
          setLoading(false);
        });
    },
    [
      isSpin,
      shipmentId,
      checkTokenExpiry,
      showSpin,
      unauthorize,
      hideSpin,
      sensorPayloadMode,
      legType,
    ]
  );

  useEffect(() => {
    fetchShipment();
  }, [fetchShipment]);

  return { shipmentData, loading, refetchShipment: fetchShipment };
};

export default useFetchSingleShipment;
